import { readArray, readNullableString, readObject, readString } from "../http/jsonDecoding";
import type { DataTypeRef } from "./workflowsApi";

/**
 * A data type's ancestry as module B gives it from the pinned closure the input is fitted in
 * (ADR-0032 D8): the type and every data type it extends, more specific first, each with the
 * classes it extends. The web app picks a view by it (`features/workflows/presentation/`)
 * and never fits by it: what an input accepts stays the server's answer.
 */
export interface TypeLineage {
  readonly type: string;
  /** Its most specific base type; `null` when it has none or two. */
  readonly base: string | null;
  readonly classes: readonly LineageClass[];
}

export interface LineageClass {
  readonly iri: string;
  /** Every class it extends, itself left out. */
  readonly extends: readonly string[];
}

export function readTypeLineage(value: unknown): TypeLineage {
  const lineage = readObject(value, "type lineage");
  return {
    type: readString(lineage, "type"),
    base: readNullableString(lineage, "base"),
    classes: readArray(lineage["classes"], "lineage classes", (item) => {
      const found = readObject(item, "lineage class");
      return { iri: readString(found, "iri"), extends: readArray(found["extends"], "extended classes", (iri) => readString({ iri }, "iri")) };
    }),
  };
}

/** The lineage the server gave for `type`; without one (an older answer), the type alone. */
export function lineageOf(type: DataTypeRef, lineages: readonly TypeLineage[]): TypeLineage {
  return lineages.find((lineage) => lineage.type === type.type) ?? { type: type.type, base: null, classes: [{ iri: type.type, extends: [] }] };
}
